<script lang="ts">
import TowerCard from '@/views/tower_visualizer/TowerCard.vue';
import { ProgramTower } from '@/types/tower/v2/Tower';
import DeleteTowerModal from '@/views/tower_dashboard/DeleteTowerModal.vue';
import { TowerType } from '@/views/tower_dashboard/types/tower-dashboard';
import { NEW_COVERAGE_TOOLTIP } from '@/views/tower_dashboard/types/tower-dashboard';
import { BOUND_COVERAGE_TOOLTIP } from '@/views/tower_dashboard/types/tower-dashboard';
import { RENEWAL_TOOLTIP } from '@/views/tower_dashboard/types/tower-dashboard';
import { reactive } from 'vue';
import { Plus } from '@lucide/vue';
import CreateTowerModal from '@/views/tower_dashboard/CreateTowerModal.vue';
import { IsUserAClient } from '@/utilities/AuthUtils';

export default {
  name: 'AccountTowerDashboard',
  components: {
    TowerCard,
  },
};
</script>

<script setup lang="ts">
import { onMounted, ref, watch, computed } from 'vue';
import { GetTowerCardsParams, GetTowersParams, towerService } from '@/services/v2/TowerService';
import { TowerCardData, TowerTypeData } from '@/views/tower_dashboard/types/tower-dashboard';
import { useRouter } from 'vue-router';
import { AppState, Commit } from '@/store';
import EditTowerModal from '@/views/tower_dashboard/EditTowerModal.vue';
import { StoreMutationType } from '@/types/StoreMutationType';
import { useStore } from 'vuex';
import { escapeRegex } from '@/utilities/tower/regex';

// ------
// props
// ------
const props = defineProps<{
  accountId: string;
  programId: string;
}>();

const emit = defineEmits<{
  viewTowers: [serializedTower: string];
}>();

const store = useStore<AppState>();

// ---------------
// reactive data
// ---------------
const router = useRouter();
const towerCards = ref<TowerCardData[]>([]);
const selectedCardIds = ref<string[]>([]);
const loading = ref(true);
const showRenameTowerModal = ref(false);
const deleteTowerState = ref<{
  show: boolean;
  highlightTowerId?: string;
  towerCard?: TowerCardData;
}>({ show: false, highlightTowerId: undefined, towerCard: undefined });
const searchFilter = ref('');
const searchTimeout = ref<NodeJS.Timeout>();
const towerTypes = ref<TowerTypeData[]>();
const editTowerId = ref<string>();
const disableTowers = ref<false>();
const newTowerModalData = reactive({
  show: false,
  type: TowerType.BoundCoverage,
});

// -----------
// lifecycles
// -----------
onMounted(async () => {
  loading.value = true;
  await fetchTowerCards();
  towerTypes.value = await towerService.getTowerTypes();
  loading.value = false;
});

// ---------
// watchers
// ---------
watch(searchFilter, () => {
  if (loading.value) {
    return;
  }
  if (searchTimeout.value) {
    clearTimeout(searchTimeout.value);
  }
  searchTimeout.value = setTimeout(() => {
    fetchTowerCards();
  }, 400);
});

// ---------
// computed
// ---------
const isFiltered = computed(() => {
  return !!searchFilter.value;
});

const isUserAClient = computed(() => {
  return IsUserAClient(store.state.user);
});

// --------
// methods
// --------
async function fetchTowerCards() {
  const params: GetTowerCardsParams = {
    searchQuery: searchFilter.value,
    accountId: Number(props.accountId),
    programId: Number(props.programId),
  };

  towerCards.value = await towerService.getTowerCards(params);

  if (isUserAClient.value) {
    towerCards.value = towerCards.value
      .filter(towerCard => towerCard.published)
      .map(towerCard => ({ ...towerCard, contributors: '' }));
  }

  for (const towerCard of towerCards.value) {
    if (towerCard.towerTypeId === TowerType.MultiTower) {
      towerCard.towerCards = [];
      const tower = (await towerService.getTowers({ towerIds: [towerCard.towerId] }))[0];
      if (tower) {
        const towerObject = JSON.parse(tower.towerObject);
        for (const object of towerObject.objects) {
          if (object['towerCard']) {
            towerCard.towerCards.push(object['towerCard']);
          }
        }
      }
    }
  }

  loading.value = false;
}

function handleTowerSelect(towerId: string, selected: boolean) {
  if (selected) {
    selectedCardIds.value.push(towerId);
  } else {
    selectedCardIds.value = selectedCardIds.value.filter((id) => id !== towerId);
  }
}

function handleEditTower(towerId: string) {
  const towerCard = towerCards.value.find((t) => t.towerId === towerId);
  if (towerCard?.towerTypeId === 5) {
    router.push('towers/' + towerId);
  } else {
    router.push('/v2/tower/' + towerId);
  }
}

function openRenameTowerModal(towerId: string) {
  const tower = towerCards.value?.filter((t) => t.towerId === towerId)[0];
  if (tower) {
    editTowerId.value = tower.towerId;
    showRenameTowerModal.value = true;
  }
}

function handleRenameTower(success: boolean) {
  if (success) {
    Commit(StoreMutationType.SET_FLASH_MESSAGE, { text: 'Tower updated successfully' });
  } else {
    Commit(StoreMutationType.SET_FLASH_MESSAGE, { text: 'Error: Could not update tower', color: 'error' });
  }

  showRenameTowerModal.value = false;
  fetchTowerCards();
}

async function handleDuplicateTower(towerId: string) {
  // find count of matching tower versions for this tower (by programId and tower type)
  // add 1 to that count to get the new version number
  const tower: ProgramTower = (await towerService.getTowers({ towerIds: [towerId] }))[0];

  if (!tower) {
    return;
  }

  const params = {
    programId: tower.programId ? Number(tower.programId) : 0,
    towerTypeId: tower.towerTypeId,
    towerName: tower.towerName,
  } as GetTowersParams;

  const towers = await towerService.getTowers(params);

  if (!towers || towers.length === 0) {
    return;
  }

  const baseVersionLabel = tower.versionLabel?.replace(/(?: \(\d+\))?$/, '') ?? '';
  const duplicateLabelRegex = new RegExp(`^${escapeRegex(baseVersionLabel)}(?: \\(\\d+\\))?$`);

  const versionLabelCount = towers.reduce((acc, t) => {
    if (t.versionLabel && t.versionLabel.match(duplicateLabelRegex)) {
      acc++;
    }
    return acc;
  }, 0);

  if (versionLabelCount === 0) {
    return;
  } else {
    tower.versionLabel = `${baseVersionLabel} (${versionLabelCount})`;
  }

  await towerService.createTower(tower);
  await fetchTowerCards();
}

function handleConfirmDeleteTower(towerCard: TowerCardData) {
  deleteTowerState.value = {
    show: true,
    highlightTowerId: towerCard.towerId,
    towerCard,
  };
}

async function handleDeleteTower(towerId?: string) {
  if (!towerId) {
    return;
  }

  await towerService.deleteTower(towerId);
  deleteTowerState.value = {
    show: false,
    highlightTowerId: undefined,
    towerCard: undefined,
  };
  await fetchTowerCards();
}

function resetFilters() {
  searchFilter.value = '';
}

async function handleCreateTowerAction() {
  emit('viewTowers', await createSerializedTower());
}

async function createSerializedTower(): Promise<string> {
  try {
    const tempCanvas = new fabric.StaticCanvas(null, {});
    const canvas = new fabric.StaticCanvas(null, {});

    const towers = await towerService.getTowers({ towerIds: selectedCardIds.value });

    for (const tower of towers) {
      const towerObject = JSON.parse(tower.towerObject);
      tempCanvas.loadFromJSON(towerObject, () => {
        tempCanvas.renderAll();

        const group = new fabric.GroupBase(tempCanvas.getObjects(), {} as fabric.GroupBaseOptions);
        group.set({
          id: tower.towerId,
          towerCard: towerCards.value.find((t) => t.towerId === tower.towerId),
          scaleX: 0.7,
          scaleY: 0.7,
        } as fabric.GroupBaseOptions);
        canvas.add(group);
        group.centerH();
        group.centerV();
        canvas.renderAll();
        tempCanvas.clear();
      });
    }

    return JSON.stringify(canvas.toJSON());
  } catch (e) {
    console.error(e);
    return '';
  }
}

function openNewTowerModal(type: TowerType) {
  newTowerModalData.type = type;
  newTowerModalData.show = true;
}

function handleCreateTower(towerId: string) {
  router.push('/v2/tower/' + towerId);
} 
</script>

<template>
  <div class="flex flex-col">
    <div class="mt-6 flex h-full flex-col">
      <!-- Overlay to disable editing while card options are open -->
      <div
        v-if="disableTowers"
        @click="$event.stopPropagation()"
        class="pointer-events-all absolute z-10 h-full w-full cursor-auto"
      ></div>

      <!-- options -->
      <div class="z-100 flex w-10/12 flex-row items-center justify-center gap-6 self-center">
        <!-- Search bar -->
        <input
          type="text"
          placeholder="Search"
          v-model="searchFilter"
          class="input w-64 border-solid shadow-xs"
        />

        <button
          v-if="isFiltered"
          @click="resetFilters"
          :disabled="!isFiltered"
          class="h-fit p-0 text-xs font-bold uppercase transition-all duration-100"
          :class="isFiltered ? 'bg-white text-red-500 hover:scale-110' : 'bg-red-50 text-gray-400'"
        >
          Reset
        </button>
        <div class="join">
          <button
            class="btn btn-primary join-item ml-8"
            :class="{ 'btn-disabled': selectedCardIds.length === 0 }"
            @click="handleCreateTowerAction"
          >
            View Towers
          </button>
        </div>
      </div>

      <!--  card container -->
      <div v-if="loading" class="mt-10 flex items-center justify-center">
        <span class="loading loading-dots loading-lg text-gray-600"></span>
      </div>
      <div
        v-else
        class="mt-8 flex w-10/12 flex-row flex-wrap items-center justify-center gap-x-8 gap-y-6 self-center sm:gap-x-4 sm:gap-y-6 lg:gap-x-4 lg:gap-y-6 xl:gap-x-10 xl:gap-y-8 2xl:gap-x-14 2xl:gap-y-10"
      >
        <!-- Tower creation cards -->
        <div
          class="flex h-56 w-80 flex-row flex-wrap items-center justify-center gap-x-6 self-center"
          v-if="!isUserAClient"
        >
          <!-- bound coverage card -->
          <div
            @click="openNewTowerModal(TowerType.BoundCoverage)"
            class="group tooltip tooltip-bottom pointer-events-auto z-30 flex cursor-pointer justify-items-center"
            :data-tip="BOUND_COVERAGE_TOOLTIP"
          >
            <div
              class="m-auto flex h-24 w-36 flex-col justify-center rounded-lg border border-solid border-blue-200 bg-white text-[#133044] shadow-md transition-all duration-150 group-hover:border-blue-200 group-hover:bg-blue-50 group-hover:shadow-none"
            >
              <div class="flex flex-col items-center gap-2 self-center p-2">
                <Plus class="text-gray-400" />
                <h2 class="text-center text-sm font-medium">Bound Coverage</h2>
              </div>
            </div>
          </div>

          <!-- new coverage card -->
          <div
            @click="openNewTowerModal(TowerType.NewCoverage)"
            class="group tooltip tooltip-bottom pointer-events-auto z-30 flex cursor-pointer justify-items-center"
            :data-tip="NEW_COVERAGE_TOOLTIP"
          >
            <div
              class="m-auto flex h-24 w-36 flex-col justify-center rounded-lg border border-solid border-orange-200 bg-white text-[#133044] shadow-md transition-all duration-150 group-hover:border-blue-200 group-hover:bg-blue-50 group-hover:shadow-none"
            >
              <div class="flex flex-col items-center gap-2 self-center p-2">
                <Plus class="text-gray-400" />
                <h2 class="text-center text-sm font-medium">New Coverage</h2>
              </div>
            </div>
          </div>

          <!-- new renewal card -->
          <div
            @click="openNewTowerModal(TowerType.Renewal)"
            class="group tooltip tooltip-bottom pointer-events-auto z-20 flex cursor-pointer justify-items-center"
            :data-tip="RENEWAL_TOOLTIP"
          >
            <div
              class="m-auto flex h-24 w-36 flex-col justify-center rounded-lg border border-solid border-red-200 bg-white text-[#133044] shadow-md transition-all duration-150 group-hover:border-blue-200 group-hover:bg-blue-50 group-hover:shadow-none"
            >
              <div class="flex flex-col items-center gap-2 self-center p-2">
                <Plus class="text-gray-400" />
                <span class="text-center text-sm font-medium">Renewal</span>
              </div>
            </div>
          </div>
        </div>

        <TowerCard
          v-for="(card, index) in towerCards"
          :key="index"
          :card-data="card"
          @click="handleTowerSelect"
          @rename="openRenameTowerModal"
          @edit="handleEditTower"
          @duplicate="handleDuplicateTower"
          @delete="handleConfirmDeleteTower"
          :highlightTowerId="deleteTowerState.highlightTowerId"
          v-model:options-open="disableTowers"
        />
      </div>

      <EditTowerModal
        v-if="showRenameTowerModal"
        :tower-id="editTowerId"
        @close="showRenameTowerModal = false"
        @update="handleRenameTower"
      />
      <DeleteTowerModal
        :show="deleteTowerState.show"
        :tower-card="deleteTowerState.towerCard"
        :handle-cancel="() => (deleteTowerState = { show: false, towerCard: undefined })"
        :handle-action="(towerId: string | undefined) => handleDeleteTower(towerId)"
      />

      <CreateTowerModal
        :show="newTowerModalData.show"
        :tower-type="newTowerModalData.type"
        :account-id="accountId"
        :program-id="programId"
        @close="newTowerModalData.show = false"
        @create="handleCreateTower"
      />
    </div>
  </div>
</template>
